import { useMemo } from 'react'
import { Patient } from '../types/patient'
import { Immunisation } from '../types/immunisation'
import { generateVaccineSchedule } from '../utils/vaccineSchedule'
import './VaccineSchedule.css'

interface VaccineScheduleProps {
  patient: Patient
  immunisations: Immunisation[]
  onRecordImmunisation?: (scheduledVaccineId: string) => void
}

export function VaccineSchedule({ patient, immunisations, onRecordImmunisation }: VaccineScheduleProps) {
  const vaccineSchedule = useMemo(() => {
    if (!patient.dateOfBirth) return null
    return generateVaccineSchedule(patient.dateOfBirth, patient.id, immunisations)
  }, [patient.dateOfBirth, patient.id, immunisations])

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    } catch {
      return dateString
    }
  }

  if (!patient.dateOfBirth) {
    return (
      <div className="vaccine-schedule-empty">
        Patient date of birth is required to generate the immunisation schedule.
      </div>
    )
  }

  if (!vaccineSchedule) {
    return <div className="vaccine-schedule-loading">Generating schedule...</div>
  }

  return (
    <div className="vaccine-schedule">
      <div className="schedule-header">
        <div>
          <h3>HSE Childhood Immunisation Schedule</h3>
          <p className="schedule-subtitle">
            Based on date of birth: {formatDate(patient.dateOfBirth)} ({vaccineSchedule.scheduleVersion === 'post-oct-2024' ? 'Post-Oct 2024' : 'Pre-Oct 2024'} schedule)
          </p>
          <p className="schedule-subtitle" style={{ fontSize: '0.9em', marginTop: '0.5em', fontStyle: 'italic' }}>
            Note: Additional vaccines (flu, PPV, Tdap for pregnancy, COVID-19) can be added manually via "Add Immunisation"
          </p>
        </div>
      </div>

      <div className="schedule-list">
        {vaccineSchedule.scheduledVaccines.map((scheduledVaccine) => (
          <div
            key={scheduledVaccine.scheduleId}
            className={`schedule-item schedule-item-${scheduledVaccine.status}`}
          >
            <div className="schedule-item-header">
              <div className="schedule-item-age">
                <strong>{scheduledVaccine.age}</strong>
                <span className="schedule-item-due-date">Due: {formatDate(scheduledVaccine.dueDate)}</span>
              </div>
              <div className="schedule-item-status">
                <span className={`badge badge-${scheduledVaccine.status}`}>
                  {scheduledVaccine.status === 'given'
                    ? 'Given'
                    : scheduledVaccine.status === 'overdue'
                    ? 'Overdue'
                    : 'Due'}
                </span>
              </div>
            </div>
            <div className="schedule-item-vaccines">
              <strong>Vaccines:</strong> {scheduledVaccine.vaccineTypes.join(', ')}
            </div>
            <div className="schedule-item-details">
              <span>Location: {scheduledVaccine.location}</span>
              <span>Injections: {scheduledVaccine.numberOfInjections}</span>
              {scheduledVaccine.givenDate && (
                <span>Given: {formatDate(scheduledVaccine.givenDate)}</span>
              )}
            </div>
            {scheduledVaccine.notes && (
              <div className="schedule-item-notes">
                <em>{scheduledVaccine.notes}</em>
              </div>
            )}
            {scheduledVaccine.immunisationIds && scheduledVaccine.immunisationIds.length > 0 && (
              <div className="schedule-item-linked">
                Linked to {scheduledVaccine.immunisationIds.length} immunisation record(s)
              </div>
            )}
            {scheduledVaccine.status !== 'given' && onRecordImmunisation && (
              <button
                type="button"
                className="btn-link small"
                onClick={() => onRecordImmunisation(scheduledVaccine.scheduleId)}
              >
                Record immunisation
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
